/**
 * The readiness panel (docs/ux/registry.md): the report type pill, the
 * outcome chip and the short sentence read from the back, the server's own
 * sentence verbatim, the six-row checklist (CHECK, STATE, WHAT IS SET, FIX)
 * rendering each check's label, state word and detail verbatim, the
 * not-decided answer, and the one action the outcome calls for. The phone
 * gets the misses only, as a card.
 */

import type { ReportTypeKind } from '@beam/core/status';
import { cn } from 'cn';
import { CheckIcon, ChevronRightIcon, CircleXIcon, PauseIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { CheckState, DoctorRecord, Readiness, ReadinessCheck } from '@/app/types';
import { QueueTabs } from '@/components/shell/QueueTabs';
import { Button, buttonVariants } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { pluralise } from '@/lib/format';
import { ReadinessChip } from './ReadinessChip';
import { STATE_WORD, TYPES, fixVerb, longType, otherTypesText, readinessSentence, shortSentence, shortType, templatesHref } from './describe';

export type ReadinessAction = { kind: 'fix'; check: ReadinessCheck } | { kind: 'none' };

export function actionFor(r: Readiness): ReadinessAction {
  if (r.outcome === 'blocked') { const first = r.checks.find((c) => c.state === 'missing'); return first ? { kind: 'fix', check: first } : { kind: 'none' }; }
  return { kind: 'none' };
}

const MARK_CLASS: Record<CheckState, string> = { met: 'text-beam-success', missing: 'text-beam-error', not_decided: 'text-beam-warning' };

export function CheckMark({ state }: { state: CheckState }) {
  const Icon = state === 'met' ? CheckIcon : state === 'missing' ? CircleXIcon : PauseIcon;
  return (
    <span data-slot="check-mark" data-state={state} className={cn('inline-flex items-center gap-1 text-[13px] font-medium', MARK_CLASS[state])}>
      <Icon aria-hidden="true" className="size-3.5" />
      {STATE_WORD[state]}
      {state === 'missing' ? <span className="sr-only">, blocks generation</span> : null}
    </span>
  );
}

export type ReadinessPanelProps = {
  doctor: DoctorRecord;
  readiness: Record<ReportTypeKind, Readiness>;
  type: ReportTypeKind;
  onType: (t: ReportTypeKind) => void;
  /** Open the panel and field a check names; null while another panel is being edited. */
  onFix: (check: ReadinessCheck) => void;
  editing: boolean;
  offline: boolean;
};

export function ReadinessPanel({ doctor, readiness, type, onType, onFix, editing, offline }: ReadinessPanelProps) {
  const r = readiness[type];
  const action = actionFor(r);
  const tabs = TYPES.map((t) => ({ value: t, label: doctor.reportTypes.includes(t) ? shortType(t) : `${shortType(t)} (not offered)` }));

  return (
    <section aria-label="Readiness" data-slot="readiness-panel" className="rounded-xl border border-beam-line bg-beam-paper">
      <div className="flex flex-col gap-3 p-4">
        <h2 className="eyebrow text-beam-text-muted">Readiness</h2>
        <QueueTabs tabs={tabs} value={type} onChange={(v) => onType(v as ReportTypeKind)} label="Report type" className="[&_[role=tab]]:text-[13px]" />
        <div className="flex flex-wrap items-center gap-3">
          <ReadinessChip outcome={r.outcome} />
          <p role="status" data-slot="readiness-sentence" className="text-[16px] font-medium leading-snug xl:text-[20px]">{shortSentence(r)}</p>
        </div>
        <p data-slot="readiness-verbatim" className="text-[13px] text-beam-text-muted">{readinessSentence(type, r)}</p>
      </div>
      <Table className="border-t border-beam-line">
        <caption className="sr-only">Readiness checks for {doctor.name}, {longType(type)}</caption>
        <TableHeader className="bg-beam-cream-deep">
          <TableRow className="hover:bg-beam-cream-deep">
            <TableHead scope="col" className="eyebrow h-8 w-[208px] px-4 text-beam-text-soft">Check</TableHead>
            <TableHead scope="col" className="eyebrow h-8 w-[120px] px-4 text-beam-text-soft">State</TableHead>
            <TableHead scope="col" className="eyebrow h-8 px-4 text-beam-text-soft">What is set</TableHead>
            <TableHead scope="col" className="eyebrow h-8 w-[96px] px-4 text-right text-beam-text-soft">Fix</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {r.checks.map((c) => (
            <TableRow key={c.key} data-slot="check-row" data-check={c.key} data-state={c.state} className="border-beam-line hover:bg-transparent">
              <TableCell className="px-4 py-3 align-top text-[13px] font-medium">{c.label}</TableCell>
              <TableCell className="px-4 py-3 align-top"><CheckMark state={c.state} /></TableCell>
              <TableCell className="whitespace-normal px-4 py-3 align-top text-[13px]">{c.detail}</TableCell>
              <TableCell className="px-4 py-3 text-right align-top">
                {c.state === 'met' || c.state === 'not_decided' ? null : c.fix === 'templates' ? (
                  <Link to={templatesHref(c, type)} className={buttonVariants({ variant: 'link', className: 'h-auto p-0 text-[13px]' })} aria-label={`Open Templates: ${c.label.toLowerCase()} for ${doctor.name}`}>{fixVerb(c, type)}</Link>
                ) : (
                  <Button type="button" variant="link" className="h-auto p-0 text-[13px]" aria-label={`${fixVerb(c, type)} ${c.label.toLowerCase()} for ${doctor.name}`} disabled={offline || editing} onClick={() => onFix(c)}>{fixVerb(c, type)}</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex flex-col gap-3 border-t border-beam-line p-4">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          {action.kind === 'fix' && action.check.fix === 'templates' ? (
            <Link to={templatesHref(action.check, type)} className={buttonVariants({ size: 'lg', className: 'h-9' })} data-primary={offline ? undefined : 'true'}>Fix: {action.check.label.toLowerCase()} in Templates</Link>
          ) : action.kind === 'fix' ? (
            editing ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" size="lg" variant="outline" className="h-9 cursor-default text-beam-text-muted hover:bg-beam-paper" aria-disabled="true" aria-describedby="readiness-action-refused" onClick={(e) => e.preventDefault()}>Fix: {action.check.label.toLowerCase()}</Button>
                </TooltipTrigger>
                <TooltipContent id="readiness-action-refused">Finish or cancel the open edit first.</TooltipContent>
              </Tooltip>
            ) : (
              <Button type="button" size="lg" className="h-9" data-primary={offline ? undefined : 'true'} disabled={offline} title={offline ? 'Paused while offline.' : undefined} onClick={() => onFix(action.check)}>Fix: {action.check.label.toLowerCase()}</Button>
            )
          ) : null}
          <p className="min-w-0 flex-1 text-[13px] text-beam-text-muted">Other types: {otherTypesText(readiness, type, doctor.reportTypes)}</p>
        </div>
      </div>
    </section>
  );
}

/** The phone's readiness: the chip, the short sentence, the misses with their details, the mets folded, and who fixes the rest. */
export function ReadinessCard({ readiness, type, onType }: { readiness: Record<ReportTypeKind, Readiness>; type: ReportTypeKind; onType: (t: ReportTypeKind) => void }) {
  const r = readiness[type];
  const open = r.checks.filter((c) => c.state !== 'met');
  const met = r.checks.filter((c) => c.state === 'met');
  return (
    <section aria-label="Readiness" data-slot="readiness-card" className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-paper p-4">
      <QueueTabs tabs={TYPES.map((t) => ({ value: t, label: shortType(t) }))} value={type} onChange={(v) => onType(v as ReportTypeKind)} label="Report type" />
      <div className="flex flex-col gap-2">
        <ReadinessChip outcome={r.outcome} />
        <p role="status" data-slot="readiness-sentence" className="text-[16px] font-medium leading-snug">{shortSentence(r)}</p>
      </div>
      <ul className="flex flex-col gap-2 text-sm">
        {open.map((c) => (
          <li key={c.key} data-slot="check-row" data-check={c.key} data-state={c.state} className="flex flex-col gap-0.5">
            <CheckMark state={c.state} />
            <span className="font-medium">{c.label}</span>
            <span className="text-[13px] text-beam-text-muted">{c.detail}</span>
          </li>
        ))}
      </ul>
      {met.length > 0 ? (
        <Collapsible>
          <CollapsibleTrigger className="flex min-h-11 items-center gap-1 text-sm text-beam-text-muted">
            <ChevronRightIcon aria-hidden="true" className="size-4 transition-transform duration-[140ms] [[data-state=open]>&]:rotate-90" />
            {pluralise(met.length, 'check')} met
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="flex flex-col gap-2 pt-2 text-sm">{met.map((c) => <li key={c.key} className="flex flex-col gap-0.5"><CheckMark state={c.state} /><span>{c.detail}</span></li>)}</ul>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      {r.outcome !== 'ready' ? <p className="text-[13px] text-beam-text-muted">These are set by an admin on a desktop; ask the coordinator.</p> : null}
    </section>
  );
}

export function InlineNote({ children }: { children: ReactNode }) {
  return <p className="text-xs text-beam-text-muted">{children}</p>;
}
